/**
 * app-context — late-bound container for the request-layer providers that are
 * APP-SCOPED (need the app's owner/services) yet must be referenced by a store
 * built at MODULE scope (ADR-001; see auth-context.ts for the auth twin).
 *
 * The store factory reads `impersonation`/`demo` providers at module scope, but
 * those providers need owner lookups (`service:impersonation`, `service:demo-
 * mode`) that don't exist until an ApplicationInstance boots. Mirroring
 * auth-context exactly: the factory wires the chain to THESE stable delegating
 * functions, and the app shell (the `auth-context` instance-initializer, which
 * holds the appInstance) installs the real owner-bound providers at boot. Every
 * read dereferences the live provider, so construction order is never
 * load-bearing.
 *
 * Apps with no impersonation/demo (tio-agent) simply never install these and
 * never pass the corresponding factory options, so the defaults below (inert)
 * apply.
 *
 * Framework-free on purpose: no Ember imports, no DI, no owner.
 */
import type { ImpersonationHandlerOptions } from './handlers/impersonation-handler.ts';
import type { ReadOnlyHandlerOptions } from './handlers/read-only-handler.ts';
import type { DemoProvider } from './demo/types.ts';

export interface AppContextProviders {
  impersonation?: ImpersonationHandlerOptions;
  readOnly?: ReadOnlyHandlerOptions;
  demo?: DemoProvider;
}

const INERT_IMPERSONATION: ImpersonationHandlerOptions = {
  isImpersonated: () => false,
  confirmMutation: () => Promise.resolve(true),
};

const INERT_READ_ONLY: ReadOnlyHandlerOptions = {
  isReadOnly: () => false,
};

const INERT_DEMO: DemoProvider = {
  isActive: () => false,
  resolve: () => null,
};

const current: { providers: AppContextProviders } = { providers: {} };

export function installAppContextProviders(providers: AppContextProviders): void {
  current.providers = providers;
}

/** Test helper — restores the inert defaults. */
export function resetAppContext(): void {
  current.providers = {};
}

/**
 * Stable impersonation provider for the request manager: delegates to whatever
 * the app installed, or stays inert (never gates a mutation) when unset.
 */
export const impersonationProvider: ImpersonationHandlerOptions = {
  isImpersonated: () => (current.providers.impersonation ?? INERT_IMPERSONATION).isImpersonated(),
  confirmMutation: () => (current.providers.impersonation ?? INERT_IMPERSONATION).confirmMutation(),
};

/**
 * Stable read-only provider for the request manager: delegates to whatever the
 * app installed, or stays inert (every write allowed) when unset.
 */
export const readOnlyProvider: ReadOnlyHandlerOptions = {
  isReadOnly: () => (current.providers.readOnly ?? INERT_READ_ONLY).isReadOnly(),
  onBlocked: (blocked) => current.providers.readOnly?.onBlocked?.(blocked),
};

/**
 * Stable demo provider for the request manager: delegates to whatever the app
 * installed, or stays inert (demo never active) when unset.
 */
export const demoProvider: DemoProvider = {
  isActive: () => (current.providers.demo ?? INERT_DEMO).isActive(),
  resolve: (request) => (current.providers.demo ?? INERT_DEMO).resolve(request),
};
